/**
 * WorkdayAdmin — admin-sida för Arbetsplatser + Rastregister.
 * Route: /workday-admin (adminOnly)
 * Två flikar: Arbetsplatser | Rastregister
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  MapPin,
  Coffee,
  Plus,
  PencilSimple,
  TrashSimple,
  Buildings,
  X,
} from "@phosphor-icons/react";

const WORKPLACE_TYPES = [
  { value: "office", label: "Kontor" },
  { value: "construction", label: "Byggplats" },
  { value: "customer_home", label: "Kundens hem" },
  { value: "employee_home", label: "Anställds hem" },
  { value: "other", label: "Övrigt" },
];

function typeLabel(v) {
  return WORKPLACE_TYPES.find((t) => t.value === v)?.label || "Övrigt";
}

function durationMinutes(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  return eh * 60 + em - (sh * 60 + sm);
}

export default function WorkdayAdmin() {
  const [tab, setTab] = useState("workplaces");
  return (
    <div className="space-y-6" data-testid="workday-admin-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-text-muted">
          Löneinställningar
        </div>
        <h1 className="font-heading text-3xl md:text-4xl mt-2">
          Arbetsplatser & Rastregister
        </h1>
        <p className="text-text-muted mt-2 text-sm max-w-2xl">
          Här definierar du platser där dina anställda stämplar in samt vilka
          raster som automatiskt dras av från deras lönegrundande arbetstid.
        </p>
      </header>

      <div className="flex gap-1 border-b border-app">
        <button
          onClick={() => setTab("workplaces")}
          data-testid="tab-workplaces"
          className={`px-4 py-3 text-sm font-medium border-b-2 flex items-center gap-2 ${
            tab === "workplaces"
              ? "border-brand text-brand"
              : "border-transparent text-text-muted hover:text-text-primary"
          }`}
        >
          <Buildings size={16} weight={tab === "workplaces" ? "fill" : "regular"} />
          Arbetsplatser
        </button>
        <button
          onClick={() => setTab("breaks")}
          data-testid="tab-breaks"
          className={`px-4 py-3 text-sm font-medium border-b-2 flex items-center gap-2 ${
            tab === "breaks"
              ? "border-brand text-brand"
              : "border-transparent text-text-muted hover:text-text-primary"
          }`}
        >
          <Coffee size={16} weight={tab === "breaks" ? "fill" : "regular"} />
          Rastregister
        </button>
      </div>

      {tab === "workplaces" ? <WorkplacesSection /> : <BreaksSection />}
    </div>
  );
}

// ============================================================================
// ARBETSPLATSER
// ============================================================================
function WorkplacesSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = ingen modal, {} = ny, {...} = redigera

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/workday/workplaces");
      setItems(res.data || []);
    } catch (e) {
      toast.error("Kunde inte hämta arbetsplatser");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (form.workplace_id) {
        await api.patch(`/workday/workplaces/${form.workplace_id}`, {
          name: form.name,
          address: form.address || null,
          workplace_type: form.workplace_type,
          active: form.active,
        });
      } else {
        await api.post("/workday/workplaces", {
          name: form.name,
          address: form.address || null,
          workplace_type: form.workplace_type,
        });
      }
      toast.success("Sparat");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte spara");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Ta bort arbetsplatsen? Anställdas förval nollställs.")) return;
    try {
      await api.delete(`/workday/workplaces/${id}`);
      toast.success("Arbetsplats borttagen");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte ta bort");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-text-muted">
          {items.length} {items.length === 1 ? "arbetsplats" : "arbetsplatser"} registrerade
        </div>
        <button
          onClick={() => setEditing({ name: "", workplace_type: "office" })}
          className="bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
          data-testid="add-workplace-btn"
        >
          <Plus size={16} weight="bold" />
          Lägg till arbetsplats
        </button>
      </div>

      {loading ? (
        <div className="text-text-muted text-sm">Laddar...</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Buildings}
          title="Inga arbetsplatser än"
          body="Lägg till minst en arbetsplats för att aktivera arbetsdag-stämpling."
        />
      ) : (
        <div className="tk-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-app text-text-muted text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-6 py-3 font-medium">Namn</th>
                <th className="text-left px-6 py-3 font-medium">Typ</th>
                <th className="text-left px-6 py-3 font-medium">Adress</th>
                <th className="text-left px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((w) => (
                <tr
                  key={w.workplace_id}
                  className="border-t border-app hover:bg-app/50"
                  data-testid={`workplace-row-${w.workplace_id}`}
                >
                  <td className="px-6 py-4 font-medium flex items-center gap-2">
                    <MapPin size={16} className="text-brand" weight="fill" />
                    {w.name}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-xs bg-app px-2 py-0.5 rounded">
                      {typeLabel(w.workplace_type)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-text-muted">{w.address || "—"}</td>
                  <td className="px-6 py-4">
                    {w.active === false ? (
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Inaktiv</span>
                    ) : (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Aktiv</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    <button
                      onClick={() => setEditing(w)}
                      className="text-text-muted hover:text-brand p-1"
                      title="Redigera"
                    >
                      <PencilSimple size={16} />
                    </button>
                    <button
                      onClick={() => remove(w.workplace_id)}
                      className="text-text-muted hover:text-red-600 p-1 ml-1"
                      title="Ta bort"
                    >
                      <TrashSimple size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <WorkplaceModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function WorkplaceModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState({
    workplace_id: initial.workplace_id || null,
    name: initial.name || "",
    address: initial.address || "",
    workplace_type: initial.workplace_type || "office",
    active: initial.active !== false,
  });
  const isEdit = !!form.workplace_id;
  return (
    <ModalWrapper onClose={onClose}>
      <div className="font-heading text-xl mb-1">
        {isEdit ? "Redigera arbetsplats" : "Ny arbetsplats"}
      </div>
      <div className="text-xs text-text-muted mb-5">
        Anställda väljer bland dessa när de stämplar in för dagen.
      </div>
      <div className="space-y-4">
        <Field label="Namn *">
          <input
            data-testid="workplace-input-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="tk-input w-full"
            placeholder="T.ex. Kontor Malmö"
            autoFocus
          />
        </Field>
        <Field label="Typ">
          <select
            data-testid="workplace-input-type"
            value={form.workplace_type}
            onChange={(e) => setForm({ ...form, workplace_type: e.target.value })}
            className="tk-input w-full"
          >
            {WORKPLACE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Adress (valfritt)">
          <input
            data-testid="workplace-input-address"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="tk-input w-full"
            placeholder="Storgatan 12, Malmö"
          />
        </Field>
        {isEdit && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Aktiv (visas för anställda)
          </label>
        )}
      </div>
      <ModalFooter
        onClose={onClose}
        onSave={() => onSave(form)}
        disabled={!form.name.trim()}
      />
    </ModalWrapper>
  );
}

// ============================================================================
// RASTREGISTER
// ============================================================================
function BreaksSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/workday/breaks");
      setItems(res.data || []);
    } catch (e) {
      toast.error("Kunde inte hämta raster");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (form.break_id) {
        await api.patch(`/workday/breaks/${form.break_id}`, {
          name: form.name,
          start_time: form.start_time,
          end_time: form.end_time,
          active: form.active,
        });
      } else {
        await api.post("/workday/breaks", {
          name: form.name,
          start_time: form.start_time,
          end_time: form.end_time,
        });
      }
      toast.success("Sparat");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte spara");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Ta bort rasten? Historiska avdrag påverkas inte.")) return;
    try {
      await api.delete(`/workday/breaks/${id}`);
      toast.success("Rast borttagen");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte ta bort");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-text-muted">
          {items.length} {items.length === 1 ? "rast" : "raster"} definierade
        </div>
        <button
          onClick={() => setEditing({ name: "", start_time: "12:00", end_time: "12:30" })}
          className="bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
          data-testid="add-break-btn"
        >
          <Plus size={16} weight="bold" />
          Lägg till rast
        </button>
      </div>

      {loading ? (
        <div className="text-text-muted text-sm">Laddar...</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Coffee}
          title="Inga raster registrerade"
          body="Lägg till t.ex. Morgonfika 09:00–09:15, Lunch 12:00–12:30 och Eftermiddagsfika 15:00–15:15."
        />
      ) : (
        <div className="tk-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-app text-text-muted text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-6 py-3 font-medium">Namn</th>
                <th className="text-left px-6 py-3 font-medium">Från</th>
                <th className="text-left px-6 py-3 font-medium">Till</th>
                <th className="text-left px-6 py-3 font-medium">Längd</th>
                <th className="text-left px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr
                  key={b.break_id}
                  className="border-t border-app hover:bg-app/50"
                  data-testid={`break-row-${b.break_id}`}
                >
                  <td className="px-6 py-4 font-medium flex items-center gap-2">
                    <Coffee size={16} className="text-brand" weight="duotone" />
                    {b.name}
                  </td>
                  <td className="px-6 py-4 font-mono">{b.start_time}</td>
                  <td className="px-6 py-4 font-mono">{b.end_time}</td>
                  <td className="px-6 py-4">{durationMinutes(b.start_time, b.end_time)} min</td>
                  <td className="px-6 py-4">
                    {b.active === false ? (
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Inaktiv</span>
                    ) : (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Aktiv</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    <button
                      onClick={() => setEditing(b)}
                      className="text-text-muted hover:text-brand p-1"
                      title="Redigera"
                    >
                      <PencilSimple size={16} />
                    </button>
                    <button
                      onClick={() => remove(b.break_id)}
                      className="text-text-muted hover:text-red-600 p-1 ml-1"
                      title="Ta bort"
                    >
                      <TrashSimple size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-brand/5 border border-brand/20 rounded-md p-4 text-sm max-w-3xl">
        <div className="font-medium mb-1">💡 Så här räknar systemet</div>
        <div className="text-text-muted leading-relaxed">
          När en anställd stämplar ut kollar systemet varje rast:
          <br />
          – Ligger rastens hela tidsfönster INUTI dagens instämpling–utstämpling? → Dras av.
          <br />
          – Är rasten helt eller delvis utanför? → Räknas inte.
          <br />
          Detta innebär att en anställd som slutar 11:00 inte får lunch (12:00–12:30) avdragen.
        </div>
      </div>

      {editing && (
        <BreakModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function BreakModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState({
    break_id: initial.break_id || null,
    name: initial.name || "",
    start_time: initial.start_time || "12:00",
    end_time: initial.end_time || "12:30",
    active: initial.active !== false,
  });
  const duration = useMemo(
    () => durationMinutes(form.start_time, form.end_time),
    [form.start_time, form.end_time]
  );
  const isEdit = !!form.break_id;
  return (
    <ModalWrapper onClose={onClose}>
      <div className="font-heading text-xl mb-1">
        {isEdit ? "Redigera rast" : "Ny rast"}
      </div>
      <div className="text-xs text-text-muted mb-5">
        Rasten dras automatiskt av från arbetsdagens brutto — om rastens hela
        fönster ligger inuti dagen.
      </div>
      <div className="space-y-4">
        <Field label="Namn *">
          <input
            data-testid="break-input-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="tk-input w-full"
            placeholder="T.ex. Morgonfika, Lunch, Eftermiddagsfika"
            autoFocus
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Från (HH:MM) *">
            <input
              data-testid="break-input-start"
              type="time"
              value={form.start_time}
              onChange={(e) => setForm({ ...form, start_time: e.target.value })}
              className="tk-input w-full"
            />
          </Field>
          <Field label="Till (HH:MM) *">
            <input
              data-testid="break-input-end"
              type="time"
              value={form.end_time}
              onChange={(e) => setForm({ ...form, end_time: e.target.value })}
              className="tk-input w-full"
            />
          </Field>
        </div>
        <div className="bg-app rounded-md px-4 py-3 text-sm">
          <span className="text-text-muted">Längd: </span>
          <span className="font-medium">{duration > 0 ? `${duration} minuter` : "⚠️ ogiltig"}</span>
        </div>
        {isEdit && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Aktiv (dras av från nya arbetsdagar)
          </label>
        )}
      </div>
      <ModalFooter
        onClose={onClose}
        onSave={() => onSave(form)}
        disabled={!form.name.trim() || duration <= 0}
      />
    </ModalWrapper>
  );
}

// ============================================================================
// Shared UI helpers
// ============================================================================
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5 text-text-muted">{label}</label>
      {children}
    </div>
  );
}

function ModalWrapper({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-app border border-app rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-text-muted hover:text-text-primary p-1"
        >
          <X size={18} />
        </button>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({ onClose, onSave, disabled }) {
  return (
    <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-app">
      <button
        onClick={onClose}
        className="px-4 py-2 rounded-md text-sm hover:bg-surface-hover"
      >
        Avbryt
      </button>
      <button
        onClick={onSave}
        disabled={disabled}
        className="bg-brand hover:bg-brand-hover disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-medium"
        data-testid="modal-save-btn"
      >
        Spara
      </button>
    </div>
  );
}

function EmptyState({ icon: Icon, title, body }) {
  return (
    <div className="tk-card p-10 text-center">
      <div className="w-14 h-14 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-4">
        <Icon size={28} weight="duotone" className="text-brand" />
      </div>
      <div className="font-heading text-lg">{title}</div>
      <div className="text-sm text-text-muted mt-1 max-w-md mx-auto">{body}</div>
    </div>
  );
}
